import { clamp } from './number'

export function hslToHex(
  hue: number,
  saturation: number,
  luminosity: number
): string {
  // Ensure input values are in valid ranges
  hue = hue % 360
  saturation = Math.max(0, Math.min(100, saturation)) / 100
  luminosity = Math.max(0, Math.min(100, luminosity)) / 100

  // Calculate RGB values
  const chroma = (1 - Math.abs(2 * luminosity - 1)) * saturation
  const huePrime = hue / 60
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1))
  const m = luminosity - chroma / 2

  let r = 0,
    g = 0,
    b = 0
  if (huePrime >= 0 && huePrime < 1) {
    r = chroma
    g = x
  } else if (huePrime >= 1 && huePrime < 2) {
    r = x
    g = chroma
  } else if (huePrime >= 2 && huePrime < 3) {
    g = chroma
    b = x
  } else if (huePrime >= 3 && huePrime < 4) {
    g = x
    b = chroma
  } else if (huePrime >= 4 && huePrime < 5) {
    r = x
    b = chroma
  } else if (huePrime >= 5 && huePrime < 6) {
    r = chroma
    b = x
  }

  // Convert to 8-bit values and add lightness adjustment
  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

function toHex(value: number) {
  return Math.round(value * 255)
    .toString(16)
    .padStart(2, '0')
}

export interface Hsl {
  hue: number
  saturation: number
  luminosity: number
}

/**
 * Parse a `#rgb` or `#rrggbb` hex colour into HSL. Returns null when
 * the value is not a hex colour (e.g. a CSS keyword or gradient).
 */
export function hexToHsl(hex: string): Hsl | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) {
    return null
  }
  let value = match[1]
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('')
  }
  const red = parseInt(value.slice(0, 2), 16) / 255
  const green = parseInt(value.slice(2, 4), 16) / 255
  const blue = parseInt(value.slice(4, 6), 16) / 255

  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const luminosity = (max + min) / 2
  const delta = max - min

  let hue = 0
  let saturation = 0
  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * luminosity - 1))
    switch (max) {
      case red:
        hue = ((green - blue) / delta) % 6
        break
      case green:
        hue = (blue - red) / delta + 2
        break
      default:
        hue = (red - green) / delta + 4
    }
    hue *= 60
    if (hue < 0) hue += 360
  }

  return {
    hue,
    saturation: saturation * 100,
    luminosity: luminosity * 100,
  }
}

/**
 * Lightness a calendar colour is anchored to in each theme so the
 * event surface reads well against the app background and its text.
 */
export const CALENDAR_EVENT_LUMINOSITY = {
  light: 84,
  dark: 26,
}

/**
 * Derive a theme-aware event background from a saved calendar
 * colour. The stored hex only really carries a hue identity (it is
 * generated at a single fixed lightness), so we keep its hue and
 * saturation but re-anchor the lightness per theme: a soft tint in
 * light mode and a deep shade in dark mode. The result is a
 * `light-dark(...)` value, so a single CSS variable adapts to the
 * active theme.
 *
 * Non-hex values (keywords, gradients) are returned unchanged.
 */
export function calendarEventBackground(colour: string): string {
  const hsl = hexToHsl(colour)
  if (!hsl) {
    return colour
  }
  // Keep enough saturation for the hue to read, but avoid neon
  // surfaces that fight the text.
  const saturation = clamp(hsl.saturation, 35, 75)
  const light = hslToHex(
    hsl.hue,
    saturation,
    CALENDAR_EVENT_LUMINOSITY.light
  )
  const dark = hslToHex(
    hsl.hue,
    saturation,
    CALENDAR_EVENT_LUMINOSITY.dark
  )
  return `light-dark(${light}, ${dark})`
}

function hueToRgb(
  primary: number,
  secondary: number,
  tempHue: number
) {
  if (tempHue < 0) tempHue += 1
  if (tempHue > 1) tempHue -= 1
  if (tempHue < 1 / 6)
    return primary + (secondary - primary) * 6 * tempHue
  if (tempHue < 1 / 2) return secondary
  if (tempHue < 2 / 3)
    return (
      primary + (secondary - primary) * (2 / 3 - tempHue) * 6
    )
  return primary
}

/**
 * Random colour generator based on known saturation / lightness
 * ======================
 *
 * credit: https://www.html-code-generator.com/javascript/random-color
 */
export function randomHueColorWithLightness(
  saturation: number,
  lightness: number
) {
  // Ensure saturation and lightness values stay within the valid range
  // saturation 20 - 100;
  // lightness 20 - 85;
  const hue = Math.floor(Math.random() * 361)
  const adjustedSaturation = Math.max(
    0,
    Math.min(100, saturation)
  )
  const adjustedLightness = Math.max(0, Math.min(100, lightness))

  const normalizedHue = hue / 360
  const normalizedSaturation = adjustedSaturation / 100
  const normalizedLightness = adjustedLightness / 100

  let red, green, blue

  if (normalizedSaturation === 0) {
    // If saturation is 0, the color is a shade of gray
    red = green = blue = normalizedLightness
  } else {
    const secondaryComponent =
      normalizedLightness < 0.5
        ? normalizedLightness * (1 + normalizedSaturation)
        : normalizedLightness +
          normalizedSaturation -
          normalizedLightness * normalizedSaturation

    const primaryComponent =
      2 * normalizedLightness - secondaryComponent

    red = hueToRgb(
      primaryComponent,
      secondaryComponent,
      normalizedHue + 1 / 3
    )
    green = hueToRgb(
      primaryComponent,
      secondaryComponent,
      normalizedHue
    )
    blue = hueToRgb(
      primaryComponent,
      secondaryComponent,
      normalizedHue - 1 / 3
    )
  }

  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`
}
